import React, {
  useCallback,
  useRef,
} from 'react';
import defer from 'lodash/defer';
import noop from 'lodash/noop';
import isNil from 'lodash/isNil';
import upperFirst from 'lodash/upperFirst';
import printJS from 'print-js';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import {
  useNotify,
} from 'core/hooks';
import CurrencyTextField from 'core/components/CurrencyTextField';
import Button from 'pages/Dashboard/components/Button';
import Dialog from 'pages/Dashboard/components/Dialog';
import Input from 'pages/Dashboard/components/Input';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import {
  Patient,
} from 'pages/Dashboard/types/patient';
import {
  validateEmail,
} from 'pages/Dashboard/utils/helper';
import {
  usePrintPatientFinalNotice,
  useSendPatientFinalNotice,
} from 'pages/Dashboard/pages/Billing/hooks';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';

const actions = ['print', 'send'] as const;
export type Action = typeof actions[number];

type FinalNoticeForm = {
  comment?: string;
  email?: string;
  monthlyAmount?: string | number | null;
};

export type FinalNoticeDialogParams = {
  type: Action;
  patient: Patient;
  patientDue: number;
};

type Props = FinalNoticeDialogParams & {
  onClose: () => void;
};

function validateMonthlyPaymentAmount(maxAllowedAmount: number) {
  return (value: unknown) => {
    const amount = Number(value);
    let result: true | string = true;
    if (!Number.isFinite(amount) || amount <= 0) {
      result = 'Monthly Payment Amount is required';
    } else if (amount > maxAllowedAmount) {
      result = 'Amount cannot exceed total balance overdue';
    }
    return result;
  };
}

export default function FinalNoticeFormDialog({
  type,
  patient,
  patientDue,
  onClose,
}: Props) {
  const { isBillingContact, email: patientContactEmail } = patient?.otherContact ?? {};
  const formProps = useForm<FinalNoticeForm>({
    mode: 'all',
    defaultValues: {
      email: (isBillingContact ? patientContactEmail : patient?.email) ?? '',
      monthlyAmount: undefined,
    },
  });
  const {
    control,
    formState: { isValid },
    handleSubmit,
    setValue,
    trigger,
    watch,
  } = formProps;

  const monthlyAmountInputRef = useRef<HTMLInputElement>(undefined);
  const monthlyAmount = watch('monthlyAmount');

  const notify = useNotify();

  const {
    isPending: isPrinting,
    mutateAsync: printFinalNotice,
  } = usePrintPatientFinalNotice();

  const {
    isPending: isSending,
    mutateAsync: sendFinalNotice,
  } = useSendPatientFinalNotice();

  const onSubmit = useCallback(async ({ email, comment, monthlyAmount }: FinalNoticeForm) => {
    const monthlyAmountValue = isNil(monthlyAmount) ? undefined : Number(monthlyAmount);
    if (type === 'send') {
      await sendFinalNotice({
        patientId: patient.patientId,
        email,
        comment,
        ...(isNil(monthlyAmountValue) ? {} : { monthlyAmount: monthlyAmountValue }),
      });
      notify({
        message: 'Final Notice sent successfully!',
        variant: 'success',
      });
    } else {
      const response = await printFinalNotice({
        patientId: patient.patientId,
        comment,
        ...(isNil(monthlyAmountValue) ? {} : { monthlyAmount: monthlyAmountValue }),
      });
      printJS({
        printable: response?.list?.[0]?.url?.value,
        type: 'pdf',
        showModal: true,
      });
    }

    onClose?.();
  }, [type, patient, onClose, notify, printFinalNotice, sendFinalNotice]);

  const actionText = upperFirst(type);
  const isLoading = isSending || isPrinting;
  const maxAllowedAmount = patientDue;

  return (
    <Dialog
      open
      title={`${actionText} Final Notice`}
      onClose={onClose}
      onConfirm={noop}
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth="md"
      className="populate-form"
    >
      <FormProvider {...formProps}>
        <div className="flex flex-col gap-form-row">
          {type === 'send' && (
            <Controller
              control={control}
              name="email"
              rules={{
                required: 'Email is required',
                validate: validateEmail,
              }}
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  {...field}
                  required
                  id="email"
                  error={invalid}
                  showErrorText={false}
                  className="w-full"
                  label="Email"
                  placeholder="Enter Email"
                />
              )}
            />
          )}
          {isNil(monthlyAmount) ? (
            <Button
              variant="text"
              color="primary"
              onClick={() => {
                setValue('monthlyAmount', 0);
                defer(() => { monthlyAmountInputRef?.current?.focus?.(); });
              }}
              className="flex items-center gap-1 !text-xs !font-medium"
            >
              <AddIcon className="!w-5 !h-5 !text-inherit" />
              Add monthly payment amount
            </Button>
          ) : (
            <Controller
              name="monthlyAmount"
              control={control}
              rules={{
                validate: validateMonthlyPaymentAmount(maxAllowedAmount),
              }}
              render={({ field: { value, onChange, ...field }, fieldState: { invalid } }) => (
                <Wrapper
                  id="final-notice-monthly-payment-amount"
                  label="Monthly Payment Amount"
                  required
                  error={invalid}
                  showErrorText={false}
                  classes={{ container: '!items-end' }}
                >
                  <CurrencyTextField
                    {...field}
                    value={value}
                    allowNegative={false}
                    onChange={(updatedValue) => {
                      if (updatedValue !== value) {
                        onChange(updatedValue);
                      }
                    }}
                  />
                  <IconButton
                    size="small"
                    className="!h-7 !w-7 relative -top-1"
                    onClick={() => {
                      setValue('monthlyAmount', undefined);
                      trigger();
                    }}
                  >
                    <CloseIcon className="!w-5 !h-5 !text-gray-500" />
                  </IconButton>
                </Wrapper>
              )}
            />
          )}
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <TextAreaWithActions
                {...field}
                className="mt-3"
                placeholder="Add Comment"
                textAreaClassName="pb-8"
                rows={3}
                id="final-notice-comment"
                trackingLabel="Final Notice Comment"
                label="Comment"
              />
            )}
          />
        </div>
        <div className="flex gap-4 justify-end">
          <Button
            variant="outlined"
            type="button"
            onClick={onClose}
          >
            Close
          </Button>
          <DisabledFormTooltip>
            <Button
              variant="contained"
              type="submit"
              color="primary"
              className="!h-full"
              disabled={!isValid || isLoading}
              onClick={handleSubmit(onSubmit)}
            >
              {isLoading ? `${actionText}...` : actionText}
            </Button>
          </DisabledFormTooltip>
        </div>
      </FormProvider>
    </Dialog>
  );
}
